import React from "react";
import { motion } from "framer-motion";
import { Users, Eye, Megaphone, AlertCircle, ArrowRight } from "lucide-react";

const GROUPS = [
  {
    id: "creators",
    icon: Users,
    label: "For Creators",
    index: "01",
    headline: "Volatile incomes. Endless overhead.",
    points: [
      "Revenue volatility from algorithmic dependency",
      "High operational overhead per asset shipped",
      "Content dilution in attention-driven feeds",
      "No real ownership of the audience built",
    ],
    solution: "Stable monetization, audience ownership, AI-assisted production.",
  },
  {
    id: "viewers",
    icon: Eye,
    label: "For Viewers",
    index: "02",
    headline: "Information overload. Zero signal.",
    points: [
      "Information fragmentation across surfaces",
      "Passive consumption habits, no recall",
      "No reward for serious, deep engagement",
      "Trust erosion in mass-market platforms",
    ],
    solution: "Curated depth, knowledge rewards, signals you can act on.",
  },
  {
    id: "advertisers",
    icon: Megaphone,
    label: "For Advertisers",
    index: "03",
    headline: "Reach without intent. Spend without lift.",
    points: [
      "Low-intent reach with weak conversion",
      "Soft signals, opaque attribution",
      "Brand-safe targeting that's still inefficient",
      "Diminishing returns on broad impressions",
    ],
    solution: "Intent-based audiences, premium adjacency, real lift.",
  },
];

export default function WhyF3() {
  return (
    <section
      id="why-f3"
      data-testid="section-why-f3"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase">
                Chapter 01 · Why F3
              </span>
            </div>
            <h2
              className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0]"
              data-testid="why-f3-title"
            >
              The internet —
              <br />
              optimized for{" "}
              <span className="text-zinc-400">virality</span>
              <br />
              — is broken
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed border-l border-white/10 pl-6">
              The current attention economy is a failing model. Creators chase
              algorithms, viewers drown in noise, and advertisers buy reach
              without intent. F3 inverts this dynamic — a media stack engineered
              for <span className="text-white">value</span>,{" "}
              <span className="text-white">signal</span>, and{" "}
              <span className="text-white">conviction</span>.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {GROUPS.map((g, i) => {
            const Icon = g.icon;
            return (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`why-card-${g.id}`}
                className="group bg-black hover:bg-zinc-950 transition-colors p-8 md:p-10 flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-violet-500/60 transition-colors">
                    <Icon className="w-5 h-5 text-zinc-300 group-hover:text-violet-300" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[11px] tracking-[0.25em] text-zinc-600">
                    {g.index} / 03
                  </span>
                </div>

                <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">
                  {g.label}
                </div>
                <h3 className="font-heading text-2xl md:text-[28px] text-white leading-tight tracking-tight mb-6">
                  {g.headline}
                </h3>

                <ul className="space-y-3 flex-1">
                  {g.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-zinc-400 leading-relaxed">
                      <AlertCircle className="w-4 h-4 mt-0.5 text-zinc-600 shrink-0" strokeWidth={1.5} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-violet-300 mb-2 flex items-center gap-2">
                    F3 Solution <ArrowRight className="w-3 h-3" strokeWidth={2} />
                  </div>
                  <p className="text-sm text-white leading-relaxed">{g.solution}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
